import React from "react";
import Icon from "../../../components/AppIcon";

const HardwareConfig = ({
  availableMics,
  availableOutputs,
  selectedOutputId,
  setAudioOutputDevice,
  selectMicDevice,
  micState,
  startMicrophone,
  recordingState,
  startRecording,
  stopRecording,
  clearRecordingResult,
  diagnostics,
  color = "primary"
}) => {
  const handleOutputDeviceChange = (e) => {
    const sinkId = e.target.value;
    setAudioOutputDevice(sinkId);
  };

  const handleMicDeviceChange = (e) => {
    const deviceId = e.target.value;
    selectMicDevice(deviceId);
    if (micState.isActive) {
      startMicrophone(deviceId);
    }
  };

  // Helper to format recording seconds into MM:SS
  const formatSecs = (secs) => {
    if (isNaN(secs) || secs <= 0) return "00:00";
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-3.5 sm:p-4 h-full flex flex-col justify-between terminal-glow font-source">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1.5 border-b border-border pb-2 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="p-1 bg-primary/10 border border-primary/20 rounded">
            <Icon name="Sliders" size={13} className="text-primary" />
          </div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
            System Hardware Routing & Recording Config
          </h3>
        </div>
        <span className="text-[8.5px] text-muted-foreground font-source uppercase font-bold px-2 py-0.5 rounded bg-zinc-950 border border-zinc-900">
          Hardware Interface v1.0
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-grow mt-3 items-stretch overflow-y-auto lg:overflow-hidden pr-0.5">
        {/* Col 1: Audio Device Selectors */}
        <div className="col-span-1 lg:col-span-6 flex flex-col justify-between pr-0 lg:pr-2 border-b lg:border-b-0 lg:border-r border-zinc-800/40 pb-4 lg:pb-0 space-y-4">
          <div className="space-y-3">
            <h4 className="text-[9.5px] font-source text-muted-foreground uppercase font-bold border-b border-zinc-900 pb-1 flex items-center space-x-1.5">
              <Icon name="Volume2" size={12} className="text-terminal-cyan" />
              <span>Input & Output Audio Routing</span>
            </h4>

            {/* Output Selector */}
            <div className="flex flex-col space-y-1.5 bg-black/30 p-2.5 rounded-lg border border-zinc-900">
              <span className="text-muted-foreground uppercase text-[8.5px] font-bold flex items-center space-x-1">
                <Icon name="Volume2" size={10} className="text-terminal-cyan" />
                <span>Master Output Speaker / Headphones / Bluetooth</span>
              </span>
              <select
                value={selectedOutputId || ""}
                onChange={handleOutputDeviceChange}
                className="bg-zinc-950 border border-zinc-800 text-foreground py-1.5 px-2 rounded text-[10px] font-fira outline-none truncate w-full cursor-pointer hover:border-zinc-700 transition"
              >
                <option value="">Default Speaker / System Output</option>
                {availableOutputs && availableOutputs.map((device, idx) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Output Device ${idx + 1}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Microphone Selector */}
            <div className="flex flex-col space-y-1.5 bg-black/30 p-2.5 rounded-lg border border-zinc-900">
              <span className="text-muted-foreground uppercase text-[8.5px] font-bold flex items-center space-x-1">
                <Icon name="Mic" size={10} className="text-primary" />
                <span>Voice Input Microphone / Headset Mic</span>
              </span>
              <select
                value={micState.selectedDeviceId || ""}
                onChange={handleMicDeviceChange}
                className="bg-zinc-950 border border-zinc-800 text-foreground py-1.5 px-2 rounded text-[10px] font-fira outline-none truncate w-full cursor-pointer hover:border-zinc-700 transition"
              >
                {availableMics && availableMics.map((device, idx) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Input Microphone ${idx + 1}`}
                  </option>
                ))}
                {(!availableMics || availableMics.length === 0) && (
                  <option value="">No Mic Detected</option>
                )}
              </select>
            </div>
          </div>

          {/* Bottom Hint */}
          <div className="text-[8.5px] text-zinc-400 font-source leading-relaxed bg-black/40 p-2.5 rounded-lg border border-zinc-900/80">
            <strong className="text-terminal-cyan font-bold">Bluetooth Connection Tip:</strong> Connect your Bluetooth speaker to your phone or system OS settings first, then select it in the Master Output Speaker dropdown above.
          </div>
        </div>

        {/* Col 2: Recording and Diagnostics */}
        <div className="col-span-1 lg:col-span-6 flex flex-col justify-between pl-0 lg:pl-1 space-y-4">
          {/* Recorder box */}
          <div className="space-y-3 bg-black/30 p-3 rounded-lg border border-zinc-900">
            <h4 className="text-[9.5px] font-source text-muted-foreground uppercase font-bold border-b border-zinc-900 pb-1 flex items-center space-x-1.5">
              <Icon name="Disc" size={12} className="text-destructive" />
              <span>Master Mix Session Recorder</span>
            </h4>

            {recordingState.isRecording ? (
              <button
                onClick={stopRecording}
                className="w-full py-2.5 bg-destructive text-white rounded-lg font-source font-bold text-xs uppercase tracking-wider flex items-center justify-center space-x-2 animate-pulse shadow-[0_0_15px_rgba(248,81,73,0.4)]"
              >
                <Icon name="Square" size={12} fill="currentColor" />
                <span>STOP RECORDING ({formatSecs(recordingState.duration)})</span>
              </button>
            ) : (
              <button
                onClick={startRecording}
                className="w-full py-2.5 bg-zinc-900 border border-zinc-800 text-muted-foreground hover:text-foreground hover:bg-zinc-800 hover:border-zinc-700 rounded-lg font-source font-bold text-xs uppercase tracking-wider flex items-center justify-center space-x-2 transition"
              >
                <Icon name="Disc" size={12} className="text-destructive animate-pulse" />
                <span>START RECORD SESSION</span>
              </button>
            )}

            {/* MP3 encode progress — a long mix takes a moment after Stop */}
            {recordingState.isEncoding && (
              <div className="bg-amber-950/30 border border-amber-800/50 rounded-lg p-2.5 space-y-1.5 text-[9px] font-source animate-fadeIn">
                <div className="flex items-center justify-between">
                  <span className="text-amber-400 font-bold">◐ Encoding MP3…</span>
                  <span className="text-amber-300 font-mono font-bold">{recordingState.encodeProgress}%</span>
                </div>
                <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden border border-zinc-950">
                  <div
                    className="h-full bg-amber-500 transition-all duration-150"
                    style={{ width: `${recordingState.encodeProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Recording Result Box */}
            {recordingState.resultUrl && (
              <div className="bg-emerald-950/30 border border-emerald-800/50 rounded-lg p-2.5 flex items-center justify-between text-[9px] font-source animate-fadeIn">
                <div className="flex flex-col">
                  <span className="text-emerald-400 font-bold flex items-center space-x-1">
                    <span>● Recording Complete</span>
                  </span>
                  <span className="text-zinc-400 mt-0.5">
                    Vocal & Music channels mixed natively
                    {recordingState.format ? ` · ${recordingState.format.toUpperCase()}` : ""}.
                  </span>
                </div>
                <div className="flex space-x-1.5">
                  <a
                    href={recordingState.resultUrl}
                    download={`dj-session-mix.${recordingState.format || "mp3"}`}
                    className="px-2.5 py-1 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-lg transition uppercase text-[8.5px] shadow"
                  >
                    Download Mix
                  </a>
                  <button
                    onClick={clearRecordingResult}
                    className="px-2 py-1 bg-zinc-800 text-zinc-400 hover:text-foreground font-bold rounded-lg transition uppercase text-[8.5px]"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Engine Diagnostics */}
          <div className="bg-black/30 p-3 rounded-lg border border-zinc-900 space-y-2 flex-grow flex flex-col justify-end">
            <h4 className="text-[9.5px] font-source text-muted-foreground uppercase font-bold flex items-center space-x-1.5">
              <Icon name="Activity" size={12} className="text-primary" />
              <span>Real-Time Engine Diagnostics</span>
            </h4>
            <div className="grid grid-cols-3 gap-2 bg-black/40 p-2.5 rounded-lg border border-zinc-900 font-fira text-[9.5px]">
              <div className="flex flex-col items-center text-center">
                <span className="text-zinc-500 uppercase text-[7.5px] font-source font-bold">CPU LOAD</span>
                <span className="text-foreground font-bold mt-0.5 text-primary">{diagnostics.cpuUsage}%</span>
              </div>
              <div className="flex flex-col items-center text-center border-x border-zinc-800/60 px-1">
                <span className="text-zinc-500 uppercase text-[7.5px] font-source font-bold">LATENCY</span>
                <span className="text-foreground font-bold mt-0.5 text-terminal-cyan">{diagnostics.latency} ms</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <span className="text-zinc-500 uppercase text-[7.5px] font-source font-bold">SAMPLING</span>
                <span className="text-foreground font-bold mt-0.5">{(diagnostics.sampleRate / 1000).toFixed(1)} kHz</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HardwareConfig;
